import React, { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { FileDown, Loader2 } from 'lucide-react';
import { formatCHF } from '../../utils/format';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('de-DE');
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return `${date.toLocaleDateString('de-DE')} ${date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  })}`;
};

const hexToRgb = (hex) => {
  if (!hex) return [16, 185, 129];
  const sanitized = hex.replace('#', '');
  if (sanitized.length !== 6) return [16, 185, 129];
  const r = parseInt(sanitized.substring(0, 2), 16);
  const g = parseInt(sanitized.substring(2, 4), 16);
  const b = parseInt(sanitized.substring(4, 6), 16);
  return [r, g, b];
};

const sanitizeFileName = (value) => (value || 'Kunde')
  .replace(/[^a-z0-9-_]+/gi, '_')
  .replace(/_+/g, '_')
  .replace(/^_+|_+$/g, '');

const buildCsvRows = ({ data, customerLabel, companyName }) => {
  const rows = [];
  rows.push(['Kundenexport']);
  rows.push(['Firma', companyName]);
  rows.push(['Kunde', customerLabel]);
  rows.push(['Exportdatum', formatDateTime(data.exported_at || new Date())]);
  rows.push([]);

  rows.push(['Termine']);
  rows.push(['Datum', 'Service', 'Trainer', 'Status', 'Preis (CHF)']);
  if (data.appointments?.length) {
    data.appointments.forEach((appt) => {
      rows.push([
        formatDateTime(appt.start_time),
        appt.service_name || '',
        appt.trainer_name || '',
        appt.status || '',
        formatCHF(appt.price ?? 0)
      ]);
    });
  } else {
    rows.push(['Keine Termine verfügbar']);
  }

  rows.push([]);
  rows.push(['Rechnungen']);
  rows.push(['Nummer', 'Datum', 'Status', 'Gesamt (CHF)', 'Fällig am']);
  if (data.invoices?.length) {
    data.invoices.forEach((invoice) => {
      rows.push([
        invoice.invoice_number || '',
        formatDate(invoice.created_at || invoice.date),
        invoice.status || '',
        formatCHF(invoice.total ?? 0),
        formatDate(invoice.due_date)
      ]);
    });
  } else {
    rows.push(['Keine Rechnungen verfügbar']);
  }

  rows.push([]);
  rows.push(['Zahlungen']);
  rows.push(['Datum', 'Betrag (CHF)', 'Status', 'Methode', 'Rechnung']);
  if (data.payments?.length) {
    data.payments.forEach((payment) => {
      rows.push([
        formatDate(payment.created_at),
        formatCHF(payment.amount ?? 0),
        payment.status || '',
        payment.payment_method || '',
        payment.invoice_number || ''
      ]);
    });
  } else {
    rows.push(['Keine Zahlungen verfügbar']);
  }

  rows.push([]);
  rows.push([`Offene Posten (Anzahl: ${data.open_items?.total_count || 0} | Gesamt: CHF ${formatCHF(data.open_items?.total_amount ?? 0)})`]);
  rows.push(['Datum', 'Service', 'Trainer', 'Status', 'Preis (CHF)']);
  if (data.open_items?.appointments?.length) {
    data.open_items.appointments.forEach((item) => {
      rows.push([
        formatDateTime(item.start_time),
        item.service_name || '',
        item.trainer_name || '',
        item.status || '',
        formatCHF(item.price ?? 0)
      ]);
    });
  } else {
    rows.push(['Keine offenen Posten verfügbar']);
  }

  return rows;
};

const QuickExportMenu = ({ customerId, customerName, disabled = false }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState(null);

  const customerLabel = useMemo(() => customerName || 'Kunde', [customerName]);

  const fetchLogoDataUrl = useCallback(async (headers) => {
    try {
      const response = await axios.get(`${API}/invoice-settings/logo`, {
        headers,
        responseType: 'blob'
      });
      const blob = response.data;
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      return null;
    }
  }, []);

  const fetchInvoiceSettings = useCallback(async (headers) => {
    try {
      const response = await axios.get(`${API}/invoice-settings`, { headers });
      return response.data;
    } catch (error) {
      return null;
    }
  }, []);

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const generatePdf = ({ data, settings, logoDataUrl }) => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const brandName = settings?.company_name || 'Fitness Studio';
    const brandColor = hexToRgb(settings?.pdf_primary_color);
    const margin = 40;
    let cursorY = 40;

    if (logoDataUrl) {
      const imageType = logoDataUrl.includes('png') ? 'PNG' : 'JPEG';
      doc.addImage(logoDataUrl, imageType, margin, cursorY, 90, 45);
    }

    doc.setFontSize(18);
    doc.text(brandName, margin + 110, cursorY + 28);
    doc.setFontSize(12);
    doc.text('Kundenexport', margin, cursorY + 70);
    doc.setFontSize(10);
    doc.text(`Kunde: ${customerLabel}`, margin, cursorY + 88);
    doc.text(`Exportdatum: ${formatDateTime(data.exported_at || new Date())}`, margin, cursorY + 104);

    cursorY += 130;

    const sections = [
      {
        title: 'Termine',
        headers: ['Datum', 'Service', 'Trainer', 'Status', 'Preis (CHF)'],
        rows: (data.appointments || []).map((appt) => [
          formatDateTime(appt.start_time),
          appt.service_name || '',
          appt.trainer_name || '',
          appt.status || '',
          formatCHF(appt.price ?? 0)
        ])
      },
      {
        title: 'Rechnungen',
        headers: ['Nummer', 'Datum', 'Status', 'Gesamt (CHF)', 'Fällig am'],
        rows: (data.invoices || []).map((invoice) => [
          invoice.invoice_number || '',
          formatDate(invoice.created_at || invoice.date),
          invoice.status || '',
          formatCHF(invoice.total ?? 0),
          formatDate(invoice.due_date)
        ])
      },
      {
        title: 'Zahlungen',
        headers: ['Datum', 'Betrag (CHF)', 'Status', 'Methode', 'Rechnung'],
        rows: (data.payments || []).map((payment) => [
          formatDate(payment.created_at),
          formatCHF(payment.amount ?? 0),
          payment.status || '',
          payment.payment_method || '',
          payment.invoice_number || ''
        ])
      },
      {
        title: `Offene Posten (Anzahl: ${data.open_items?.total_count || 0} | Gesamt: CHF ${formatCHF(data.open_items?.total_amount ?? 0)})`,
        headers: ['Datum', 'Service', 'Trainer', 'Status', 'Preis (CHF)'],
        rows: (data.open_items?.appointments || []).map((item) => [
          formatDateTime(item.start_time),
          item.service_name || '',
          item.trainer_name || '',
          item.status || '',
          formatCHF(item.price ?? 0)
        ])
      }
    ];

    sections.forEach((section) => {
      doc.setFontSize(12);
      doc.text(section.title, margin, cursorY);
      cursorY += 10;
      const bodyRows = section.rows.length
        ? section.rows
        : [[
          'Keine Daten verfügbar',
          '',
          '',
          '',
          ''
        ]];

      autoTable(doc, {
        startY: cursorY,
        head: [section.headers],
        body: bodyRows,
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: brandColor, textColor: [255, 255, 255] },
        theme: 'grid',
        margin: { left: margin, right: margin }
      });

      cursorY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 22 : cursorY + 80;
    });

    const fileName = `Kundenexport_${sanitizeFileName(customerLabel)}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const generateCsv = ({ data, settings }) => {
    const companyName = settings?.company_name || 'Fitness Studio';
    const rows = buildCsvRows({ data, customerLabel, companyName });
    const csv = Papa.unparse(rows, { delimiter: ';' });
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const fileName = `Kundenexport_${sanitizeFileName(customerLabel)}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadFile(blob, fileName);
  };

  const handleExport = async (format) => {
    if (!customerId || disabled || isExporting) return;
    setIsExporting(true);
    setExportFormat(format);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [exportResponse, settings, logoDataUrl] = await Promise.all([
        axios.get(`${API}/customers/${customerId}/export-data`, { headers }),
        fetchInvoiceSettings(headers),
        fetchLogoDataUrl(headers)
      ]);

      const payload = exportResponse.data;

      if (format === 'pdf') {
        generatePdf({ data: payload, settings, logoDataUrl });
      } else {
        generateCsv({ data: payload, settings });
      }

      toast.success(`Export (${format.toUpperCase()}) erstellt`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export fehlgeschlagen');
    } finally {
      setIsExporting(false);
      setExportFormat(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={disabled || isExporting}
          data-testid="quick-export-menu-button"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" data-testid="quick-export-loading-icon" />
          ) : (
            <FileDown className="h-4 w-4" />
          )}
          <span data-testid="quick-export-button-label">
            {isExporting ? `Export läuft (${exportFormat?.toUpperCase() || '...'})` : 'Quick Export'}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44" data-testid="quick-export-menu">
        <DropdownMenuItem
          onClick={() => handleExport('pdf')}
          className="cursor-pointer"
          data-testid="quick-export-pdf"
        >
          PDF exportieren
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport('csv')}
          className="cursor-pointer"
          data-testid="quick-export-csv"
        >
          CSV exportieren
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default QuickExportMenu;
